// Shared client helpers for reading & summarizing wearable_metrics rows.
// Used by the Today's Insights wearable card and the Symptom Intelligence Report PDF.
import { supabase } from "@/integrations/supabase/client";
import { localDateKey, parseLocalDateKey } from "./dailyLogs";

export type WearableMetricRow = {
  metric_date: string;
  provider: string;
  sleep_duration_minutes: number | null;
  sleep_quality_score: number | null;
  hrv_ms: number | null;
  resting_heart_rate: number | null;
  skin_temperature_c: number | null;
  respiratory_rate?: number | null;
  steps: number | null;
  active_minutes: number | null;
  /** VO2max (ml/kg/min). Pulled from raw->>'vo2_max'; null when missing. */
  vo2_max: number | null;
};

export async function fetchWearableMetrics(
  userId: string,
  days = 30
): Promise<WearableMetricRow[]> {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - (days - 1));
  const startKey = localDateKey(start);
  const endKey = localDateKey(end);
  const { data, error } = await supabase
    .from("wearable_metrics")
    .select(
      "metric_date, provider, sleep_duration_minutes, sleep_quality_score, hrv_ms, resting_heart_rate, skin_temperature_c, respiratory_rate, steps, active_minutes, vo2_max:raw->>vo2_max"
    )
    .eq("user_id", userId)
    .gte("metric_date", startKey)
    .lte("metric_date", endKey)
    .order("metric_date", { ascending: true });
  if (error) {
    console.warn("[wearableMetrics] fetch failed", error.message);
    return [];
  }
  // Coerce vo2_max (returned as text from raw JSON) to number | null.
  return ((data || []) as any[]).map((r) => {
    const v = r.vo2_max;
    const n = typeof v === "number" ? v : v != null ? Number(v) : NaN;
    return { ...r, vo2_max: Number.isFinite(n) ? n : null } as WearableMetricRow;
  });
}

// ---------- summary helpers ----------
function nums(values: (number | null | undefined)[]): number[] {
  return values.filter((v): v is number => typeof v === "number" && Number.isFinite(v));
}
function avg(values: number[]): number | null {
  if (!values.length) return null;
  return values.reduce((s, v) => s + v, 0) / values.length;
}
function lastN<T>(arr: T[], n: number): T[] {
  return arr.slice(Math.max(0, arr.length - n));
}

export function formatSleepDuration(min: number | null | undefined): string {
  if (typeof min !== "number" || !Number.isFinite(min) || min <= 0) return "—";
  const h = Math.floor(min / 60);
  const m = Math.round(min - h * 60);
  return `${h}h ${m}m`;
}

export type WearableTodaySummary = {
  hasAny: boolean;
  /** @deprecated — single-provider label. Kept for back-compat; use `providers`. */
  provider: string | null;
  /** Distinct providers whose rows actually feed the rendered tiles. */
  providers: string[];
  latestDate: string | null;
  sleep: { duration: number | null; quality: number | null; date: string | null; provider: string | null };
  restingHr: { value: number | null; date: string | null; provider: string | null };
  hrv7dAvg: number | null;
  skinTemp: { value: number | null; date: string | null; provider: string | null };
  /** Latest non-null respiratory rate (breaths/min). */
  respiratoryRate: { value: number | null; date: string | null; provider: string | null };
  /** Latest non-null VO2max (ml/kg/min) from raw->>'vo2_max'. */
  vo2Max: { value: number | null; date: string | null; provider: string | null };
  /** Yesterday's daytime activity (steps + active minutes). Distinct from
   * overnight signals — labeled separately in the UI. */
  yesterdayActivity: { steps: number | null; activeMinutes: number | null; date: string | null; provider: string | null };
};

// Score a row by how many of the canonical metric fields are non-null.
// Used to pick "the best row" when multiple providers cover the same date.
const SCORED_FIELDS: (keyof WearableMetricRow)[] = [
  "sleep_duration_minutes",
  "sleep_quality_score",
  "hrv_ms",
  "resting_heart_rate",
  "skin_temperature_c",
  "respiratory_rate",
  "steps",
  "active_minutes",
  "vo2_max",
];
function richness(r: WearableMetricRow): number {
  let n = 0;
  for (const f of SCORED_FIELDS) if (r[f] != null) n++;
  return n;
}

// For a given field, return the row that should source the tile:
//   - Find the most recent metric_date that has ANY row with the field non-null.
//   - Among that date's rows with the field non-null, pick the one with the
//     most non-null metric fields overall (tie → first encountered).
// Returns null if no row anywhere has the field.
function pickRowForField<K extends keyof WearableMetricRow>(
  rows: WearableMetricRow[],
  field: K,
): WearableMetricRow | null {
  // Find latest date that has the field.
  let latestDate: string | null = null;
  for (let i = rows.length - 1; i >= 0; i--) {
    if (rows[i][field] != null) { latestDate = rows[i].metric_date; break; }
  }
  if (!latestDate) return null;
  const candidates = rows.filter((r) => r.metric_date === latestDate && r[field] != null);
  let best = candidates[0];
  let bestScore = richness(best);
  for (let i = 1; i < candidates.length; i++) {
    const s = richness(candidates[i]);
    if (s > bestScore) { best = candidates[i]; bestScore = s; }
  }
  return best;
}

// Pick the row whose metric_date is YESTERDAY (local-TZ) and whose `field`
// is non-null. Tile must show yesterday's COMPLETED total — not the most
// recent partial row. Returns null when there is no yesterday row for this
// field; the caller must then render no value (no silent fallback).
function pickYesterdayRowForField<K extends keyof WearableMetricRow>(
  rows: WearableMetricRow[],
  field: K,
): WearableMetricRow | null {
  const y = new Date();
  y.setDate(y.getDate() - 1);
  const yKey = localDateKey(y);
  const candidates = rows.filter((r) => r.metric_date === yKey && r[field] != null);
  if (!candidates.length) return null;
  let best = candidates[0];
  let bestScore = richness(best);
  for (let i = 1; i < candidates.length; i++) {
    const s = richness(candidates[i]);
    if (s > bestScore) { best = candidates[i]; bestScore = s; }
  }
  return best;
}

export function summarizeForToday(rows: WearableMetricRow[]): WearableTodaySummary {
  const empty: WearableTodaySummary = {
    hasAny: false,
    provider: null,
    providers: [],
    latestDate: null,
    sleep: { duration: null, quality: null, date: null, provider: null },
    restingHr: { value: null, date: null, provider: null },
    hrv7dAvg: null,
    skinTemp: { value: null, date: null, provider: null },
    respiratoryRate: { value: null, date: null, provider: null },
    vo2Max: { value: null, date: null, provider: null },
    yesterdayActivity: { steps: null, activeMinutes: null, date: null, provider: null },
  };
  if (!rows.length) return empty;
  // Sleep duration + quality should come from the same row (one provider's
  // sleep record). Use sleep_duration_minutes as the anchor.
  const sleepRow = pickRowForField(rows, "sleep_duration_minutes");
  const hrvRow = pickRowForField(rows, "hrv_ms");
  const rhrRow = pickRowForField(rows, "resting_heart_rate");
  const skinRow = pickRowForField(rows, "skin_temperature_c");
  const respRow = pickRowForField(rows, "respiratory_rate");
  const vo2Row = pickRowForField(rows, "vo2_max");
  // Activity: prefer a row with steps; otherwise the active_minutes row.
  // If both exist on the same latest date, prefer the richer row.
  // Activity tiles (steps + active minutes) must show YESTERDAY'S COMPLETED
  // totals — never today's partial counters. Pick strictly the yesterday-local
  // row; if there is no row for yesterday, render nothing for that tile.
  const stepsRow = pickYesterdayRowForField(rows, "steps");
  const amRow = pickYesterdayRowForField(rows, "active_minutes");
  // Prefer the richer of the two (same date by construction); otherwise
  // whichever exists. Used only for the provider label / shared date.
  let activityRow: WearableMetricRow | null = null;
  if (stepsRow && amRow) {
    activityRow = richness(stepsRow) >= richness(amRow) ? stepsRow : amRow;
  } else {
    activityRow = stepsRow ?? amRow;
  }
  const last7 = lastN(rows, 7);
  const hrv7dAvg = avg(nums(last7.map((r) => r.hrv_ms)));

  // Providers actually feeding rendered tiles. Order: appearance.
  const providers: string[] = [];
  const push = (p?: string | null) => {
    if (p && !providers.includes(p)) providers.push(p);
  };
  if (sleepRow?.sleep_duration_minutes != null) push(sleepRow.provider);
  if (hrvRow?.hrv_ms != null) push(hrvRow.provider);
  if (rhrRow?.resting_heart_rate != null) push(rhrRow.provider);
  if (skinRow?.skin_temperature_c != null) push(skinRow.provider);
  if (respRow?.respiratory_rate != null) push(respRow.provider);
  if (vo2Row?.vo2_max != null) push(vo2Row.provider);
  if (activityRow && (activityRow.steps != null || activityRow.active_minutes != null)) push(activityRow.provider);

  const latestDate = rows[rows.length - 1].metric_date;
  return {
    hasAny: true,
    provider: providers[0] ?? rows[rows.length - 1].provider,
    providers,
    latestDate,
    sleep: {
      duration: sleepRow?.sleep_duration_minutes ?? null,
      quality: sleepRow?.sleep_quality_score ?? null,
      date: sleepRow?.metric_date ?? null,
      provider: sleepRow?.provider ?? null,
    },
    restingHr: {
      value: rhrRow?.resting_heart_rate ?? null,
      date: rhrRow?.metric_date ?? null,
      provider: rhrRow?.provider ?? null,
    },
    hrv7dAvg,
    skinTemp: {
      value: skinRow?.skin_temperature_c ?? null,
      date: skinRow?.metric_date ?? null,
      provider: skinRow?.provider ?? null,
    },
    respiratoryRate: {
      value: respRow?.respiratory_rate ?? null,
      date: respRow?.metric_date ?? null,
      provider: respRow?.provider ?? null,
    },
    vo2Max: {
      value: vo2Row?.vo2_max ?? null,
      date: vo2Row?.metric_date ?? null,
      provider: vo2Row?.provider ?? null,
    },
    yesterdayActivity: {
      steps: stepsRow?.steps ?? null,
      activeMinutes: amRow?.active_minutes ?? null,
      date: activityRow?.metric_date ?? null,
      provider: activityRow?.provider ?? null,
    },
  };
}

// ---------- doctor-report trend summary ----------
export type TrendSummary = {
  field: "sleep_duration_minutes" | "sleep_quality_score" | "resting_heart_rate" | "hrv_ms" | "skin_temperature_c";
  label: string;
  unit: string;
  avg: number | null;
  min: number | null;
  max: number | null;
  n: number;
  direction: "improving" | "worsening" | "stable" | "insufficient";
  /** % change first-half mean → second-half mean (rounded). null when baseline is near zero (e.g. skin temp delta) — use absDelta instead. */
  pct: number | null;
  /** Absolute change (second-half mean − first-half mean) in the metric's native unit. */
  absDelta: number | null;
};

const FIELD_LABEL: Record<TrendSummary["field"], { label: string; unit: string; betterWhen: "higher" | "lower" | "neutral" }> = {
  sleep_duration_minutes: { label: "Sleep duration", unit: "min", betterWhen: "higher" },
  sleep_quality_score: { label: "Sleep quality", unit: "/100", betterWhen: "higher" },
  resting_heart_rate: { label: "Resting heart rate", unit: "bpm", betterWhen: "lower" },
  hrv_ms: { label: "HRV (RMSSD)", unit: "ms", betterWhen: "higher" },
  skin_temperature_c: { label: "Skin temperature (delta)", unit: "°C", betterWhen: "neutral" },
};

export function trendFor(rows: WearableMetricRow[], field: TrendSummary["field"]): TrendSummary {
  const vals = nums(rows.map((r) => r[field] as number | null));
  const meta = FIELD_LABEL[field];
  if (vals.length === 0) {
    return { field, label: meta.label, unit: meta.unit, avg: null, min: null, max: null, n: 0, direction: "insufficient", pct: null, absDelta: null };
  }
  const a = avg(vals)!;
  const mn = Math.min(...vals);
  const mx = Math.max(...vals);
  let direction: TrendSummary["direction"] = "insufficient";
  let pct: number | null = null;
  let absDelta: number | null = null;
  if (vals.length >= 6) {
    const half = Math.floor(vals.length / 2);
    const first = vals.slice(0, half);
    const second = vals.slice(half);
    const fAvg = avg(first)!;
    const sAvg = avg(second)!;
    absDelta = sAvg - fAvg;
    // Skin temperature is already a delta; computing % off a near-zero baseline blows up.
    // Skip pct for skin_temperature_c and any other field where |baseline| is < 1 unit.
    const baselineMag = Math.abs(fAvg);
    if (field !== "skin_temperature_c" && baselineMag >= 1) {
      pct = Math.round((absDelta / baselineMag) * 100);
    }
    // Tolerance: 5% of baseline normally; absolute 0.2°C for skin temp delta.
    const tol = field === "skin_temperature_c" ? 0.2 : Math.max(baselineMag * 0.05, 0.01);
    if (Math.abs(absDelta) <= tol) direction = "stable";
    else if (meta.betterWhen === "higher") direction = absDelta > 0 ? "improving" : "worsening";
    else if (meta.betterWhen === "lower") direction = absDelta < 0 ? "improving" : "worsening";
    else direction = "stable"; // neutral fields don't have improving/worsening
  }
  return { field, label: meta.label, unit: meta.unit, avg: a, min: mn, max: mx, n: vals.length, direction, pct, absDelta };
}


// ---------- skin-temp x hot-flash correlation ----------
// For each day with both a skin_temp reading and a hot-flash event count,
// flag days where skin_temp is elevated (≥ +0.5°C above the rolling baseline
// from the prior 7 days) AND a hot flash was logged. Returns the % of
// hot-flash days that coincided with an elevated skin temp.
export type SkinTempHotFlashCorrelation = {
  daysWithBoth: number;
  hotFlashDays: number;
  elevatedHotFlashDays: number;
  pctOnElevatedDays: number | null;
  flaggedDates: string[]; // human-readable "Mon D"
};

export function correlateSkinTempHotFlash(
  rows: WearableMetricRow[],
  hfCountByDate: Record<string, number>
): SkinTempHotFlashCorrelation {
  const byDate = new Map<string, number>();
  for (const r of rows) {
    if (typeof r.skin_temperature_c === "number") byDate.set(r.metric_date, r.skin_temperature_c);
  }
  const sortedDates = [...byDate.keys()].sort();
  const flagged: string[] = [];
  let daysWithBoth = 0;
  let hotFlashDays = 0;
  let elevatedHotFlashDays = 0;

  for (let i = 0; i < sortedDates.length; i++) {
    const dk = sortedDates[i];
    const todayVal = byDate.get(dk)!;
    const hfCount = hfCountByDate[dk] || 0;
    if (hfCount > 0) hotFlashDays++;
    // rolling baseline: prior up to 7 days
    const baselineWindow: number[] = [];
    for (let j = Math.max(0, i - 7); j < i; j++) {
      const v = byDate.get(sortedDates[j]);
      if (typeof v === "number") baselineWindow.push(v);
    }
    if (baselineWindow.length < 3) continue; // need a baseline
    daysWithBoth++;
    const base = avg(baselineWindow)!;
    const isElevated = todayVal - base >= 0.5;
    if (hfCount > 0 && isElevated) {
      elevatedHotFlashDays++;
      const d = parseLocalDateKey(dk);
      flagged.push(d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : dk);
    }
  }
  const pctOnElevatedDays = hotFlashDays > 0 ? Math.round((elevatedHotFlashDays / hotFlashDays) * 100) : null;
  return { daysWithBoth, hotFlashDays, elevatedHotFlashDays, pctOnElevatedDays, flaggedDates: flagged };
}

// ---------- wearable-derived stress (display-only) ----------
// READ already-stored hrv_proxy stress from daily_triggers and summarise it
// the same way trendFor() summarises wearable_metrics rows. Single source of
// truth: the trigger engine wrote these values via mapStressFromHrv(); this
// helper never re-derives, so the wearable display and the trigger section
// can never diverge. Higher stress = worse (direction polarity below).
export type WearableStressSummary = {
  /** Latest hrv_proxy stress reading (1-4), or null if none in window. */
  latest: { value: 1 | 2 | 3 | 4 | null; date: string | null };
  /** Window aggregates over up to `days` of daily_triggers rows with
   *  stress_source='hrv_proxy' and stress_level is not null. */
  n: number;
  avg: number | null;
  min: number | null;
  max: number | null;
  /** Direction over the window. HIGHER stress is worse, so a rising trend
   *  is "worsening", a falling trend is "improving". "insufficient" when
   *  the window has fewer than 6 readings (matches trendFor()). */
  direction: "improving" | "worsening" | "stable" | "insufficient";
  /** Absolute change (second-half mean − first-half mean) on the 1-4 scale. */
  absDelta: number | null;
};

const STRESS_LABEL: Record<1 | 2 | 3 | 4, string> = {
  1: "Low",
  2: "Mild",
  3: "Elevated",
  4: "High",
};

export function stressLabelFor(level: number | null | undefined): string {
  if (level == null) return "—";
  const rounded = Math.max(1, Math.min(4, Math.round(level))) as 1 | 2 | 3 | 4;
  return STRESS_LABEL[rounded];
}

export async function fetchWearableStress(
  userId: string,
  days = 30,
): Promise<WearableStressSummary> {
  const empty: WearableStressSummary = {
    latest: { value: null, date: null },
    n: 0, avg: null, min: null, max: null,
    direction: "insufficient", absDelta: null,
  };
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - (days - 1));
  const startKey = localDateKey(start);
  const endKey = localDateKey(end);
  const { data, error } = await supabase
    .from("daily_triggers")
    .select("log_date, stress_level, stress_source")
    .eq("user_id", userId)
    .eq("stress_source", "hrv_proxy")
    .gte("log_date", startKey)
    .lte("log_date", endKey)
    .order("log_date", { ascending: true });
  if (error) {
    console.warn("[wearableMetrics] hrv_proxy stress fetch failed", error.message);
    return empty;
  }
  const rows = (data || []).filter(
    (r: any) => typeof r.stress_level === "number" && r.stress_level >= 1 && r.stress_level <= 4
  ) as { log_date: string; stress_level: number }[];
  if (!rows.length) return empty;
  const vals = rows.map((r) => r.stress_level);
  const a = vals.reduce((s, v) => s + v, 0) / vals.length;
  const mn = Math.min(...vals);
  const mx = Math.max(...vals);
  const latestRow = rows[rows.length - 1];
  const out: WearableStressSummary = {
    latest: {
      value: Math.max(1, Math.min(4, Math.round(latestRow.stress_level))) as 1 | 2 | 3 | 4,
      date: latestRow.log_date,
    },
    n: vals.length,
    avg: a,
    min: mn,
    max: mx,
    direction: "insufficient",
    absDelta: null,
  };
  if (vals.length >= 6) {
    const half = Math.floor(vals.length / 2);
    const fAvg = vals.slice(0, half).reduce((s, v) => s + v, 0) / half;
    const sAvg = vals.slice(half).reduce((s, v) => s + v, 0) / (vals.length - half);
    const absDelta = sAvg - fAvg;
    out.absDelta = absDelta;
    // Tolerance: 0.3 on the 1-4 scale (≈7.5%).
    if (Math.abs(absDelta) <= 0.3) out.direction = "stable";
    else out.direction = absDelta > 0 ? "worsening" : "improving"; // higher = worse
  }
  return out;
}
